import { motion } from 'framer-motion';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '../../../../../../context/ThemeContext.jsx';
import {
    formatCurrency,
    formatDateInCompanyTimeZone
} from '../../../../../../utils/format';
import { richTextToPlainText } from '../../../../../../utils/richText';

const DiscountCard = ({ discount, isSelected, formatMoney, onToggle, disabled = false }) => {
    const { activeMode } = useTheme() || {};
    const companyState = useSelector((state) => state?.companyReducer?.company);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const isDarkMode = activeMode === 'dark';
    const surface = isDarkMode ? 'var(--flex-surface)' : '#fff';
    const selectedSurface = isDarkMode ? 'var(--flex-primary-soft)' : 'var(--flex-primary-soft, #eff6ff)';
    const borderColor = isDarkMode ? 'var(--flex-border)' : '#e2e8f0';
    const textColor = isDarkMode ? 'var(--flex-text)' : '#0f172a';
    const mutedTextColor = isDarkMode ? 'var(--flex-text-muted)' : '#64748b';
    const subtleTextColor = isDarkMode ? 'var(--flex-text-subtle)' : '#94a3b8';
    const pillBg = isDarkMode ? 'var(--flex-surface-soft)' : '#f1f5f9';
    const isInteractive = !disabled && typeof onToggle === 'function';
    const isPercent = discount.applyType === 'PERCENT';
    const discountValue = isPercent
        ? `${discount.percentage}%`
        : (formatMoney ? formatMoney(discount.amount) : formatCurrency(discount.amount, companyState));
    // Stored as rich text - strip the markup before measuring and truncating.
    const description = richTextToPlainText(discount.description);
    const DESC_LIMIT = 90;
    const shouldTruncate = description.length > DESC_LIMIT;
    const displayDesc = showFullDescription || !shouldTruncate
        ? description
        : description.slice(0, DESC_LIMIT) + '…';

    const expiryDate = formatDateInCompanyTimeZone(
        discount.timePeriod?.effectiveDate?.localEndDate,
        companyState,
        'MMM d, yyyy',
        null
    );

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            role={isInteractive ? "button" : undefined}
            tabIndex={isInteractive ? 0 : -1}
            aria-pressed={isInteractive ? isSelected : undefined}
            onClick={() => {
                if (isInteractive) onToggle(discount);
            }}
            onKeyDown={(e) => {
                if (!isInteractive) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggle(discount);
                }
            }}
            style={{
                position: 'relative',
                borderRadius: 14,
                overflow: 'hidden',
                border: isSelected ? '2px solid var(--flex-primary, #3b82f6)' : `1.5px solid ${borderColor}`,
                background: isSelected ? selectedSurface : surface,
                transition: 'all 0.16s ease',
                userSelect: 'none',
                cursor: disabled ? 'not-allowed' : isInteractive ? 'pointer' : 'default',
                opacity: disabled ? 0.7 : 1,
            }}
        >
            {/* Left stripe */}
            <div style={{
                position: 'absolute',
                left: 0, top: 0, bottom: 0,
                width: 3,
                borderRadius: '14px 0 0 14px',
                background: isSelected
                    ? 'linear-gradient(to bottom, #10b981, #14b8a6)'
                    : 'linear-gradient(to bottom, var(--flex-primary, #3b82f6), var(--flex-accent, #6366f1))',
            }} />

            {/* Card body */}
            <div style={{ paddingLeft: 14, paddingRight: 12, paddingTop: 12, paddingBottom: 10 }}>

                {/* Badge + Full Name */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                    {/* Value badge */}
                    <div style={{
                        flexShrink: 0,
                        padding: '5px 9px',
                        borderRadius: 9,
                        fontSize: 15,
                        fontWeight: 700,
                        lineHeight: 1,
                        background: isSelected ? '#d1fae5' : 'var(--flex-primary-soft, #dbeafe)',
                        color: isSelected ? '#065f46' : 'var(--flex-primary-hover, #1e40af)',
                    }}>
                        {discountValue}
                        <span style={{ fontSize: 10, fontWeight: 700, marginLeft: 1 }}>OFF</span>
                    </div>

                    {/* Name — full, wraps naturally */}
                    <p style={{
                        flex: 1,
                        fontSize: 12,
                        fontWeight: 600,
                        color: textColor,
                        lineHeight: 1.45,
                        paddingTop: 3,
                        wordBreak: 'break-word',
                        margin: 0,
                    }}>
                        {discount.name}
                    </p>

                    <div style={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        border: isSelected ? '1px solid #10b981' : '1px solid #cbd5e1',
                        background: isSelected ? '#10b981' : surface,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: 2,
                    }}>
                        {isSelected && (
                            <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                    </div>
                </div>

                {/* Code pill */}
                {discount.code && (
                    <div style={{ marginBottom: 7 }}>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '3px 8px',
                            background: pillBg,
                            border: `1px dashed ${isDarkMode ? 'var(--flex-border-strong)' : '#cbd5e1'}`,
                            borderRadius: 6,
                            fontFamily: 'monospace',
                            fontSize: 10,
                            fontWeight: 700,
                            color: isDarkMode ? 'var(--flex-text-muted)' : '#475569',
                            letterSpacing: '0.06em',
                        }}>
                            <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            {discount.code}
                        </span>
                    </div>
                )}

                {/* Description — truncated with Read More / View Less */}
                {description && (
                    <div style={{ marginBottom: 6 }}>
                        <p style={{
                            fontSize: 11,
                            color: mutedTextColor,
                            lineHeight: 1.55,
                            wordBreak: 'break-word',
                            margin: 0,
                        }}>
                            {displayDesc}
                        </p>
                        {shouldTruncate && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowFullDescription(prev => !prev);
                                }}
                                style={{
                                    marginTop: 3,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: 'var(--flex-primary, #2563eb)',
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    display: 'block',
                                }}
                            >
                                {showFullDescription ? 'View less' : 'Read more'}
                            </button>
                        )}
                    </div>
                )}

                {/* Expiry */}
                {expiryDate && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 10,
                        color: subtleTextColor,
                        marginTop: 4,
                    }}>
                        <svg width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Expires {expiryDate}
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default DiscountCard;
